import '../styles/pages.scss';
import {useEffect, useRef, useState} from "react";
import {useLocation} from 'react-router-dom';
import {getRecordsByDate, getStrategies, checkHealth} from "../lib/beCall";
import {
    Box,
    Flex,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    SimpleGrid,
    Heading,
    Divider
} from "@chakra-ui/react";
import Toolbar from "../components/toolbar/toolbar";
import OfflineScreen from "../components/offlineScreen/offlineScreen";
import {useDate} from "../contexts/DateContext";

// Estrae il valore numerico da stringhe tipo "194363.00 USD"
const parseAmount = (str) => {
    if (!str || typeof str !== 'string') return 0;
    const match = str.match(/([\d_]+\.?\d*)/);
    if (!match) return 0;
    return parseFloat(match[1].replace(/_/g, ''));
};

const formatNumber = (num) => {
    return new Intl.NumberFormat('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(num);
};

// Tabella generica per array di oggetti
const GenericTable = ({items}) => {
    if (!items || items.length === 0) return null;
    const keys = Object.keys(items[0]);
    return (
        <TableContainer borderRadius="lg" border="1px" borderColor="gray.200" bg="white">
            <Table size="sm">
                <Thead bg="gray.50">
                    <Tr>
                        {keys.map(key => <Th key={key}>{key}</Th>)}
                    </Tr>
                </Thead>
                <Tbody>
                    {items.map((item, i) => (
                        <Tr key={i} _hover={{bg: "gray.50"}}>
                            {keys.map(key => (
                                <Td key={key} fontSize="sm">
                                    {typeof item[key] === 'object' ? JSON.stringify(item[key]) : String(item[key] ?? '-')}
                                </Td>
                            ))}
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
};

const AccountBalances = () => {
    const location = useLocation();
    const {
        selectedDate: lastDay,
        setSelectedDate: setLastDay,
        isPolling,
        consecutiveErrors,
        setConsecutiveErrors,
        hasConnectedOnce,
        setHasConnectedOnce
    } = useDate();

    const MAX_CONSECUTIVE_ERRORS = 50;
    const [snapshot, setSnapshot] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [isOffline, setIsOffline] = useState(false);
    const [isSlowConnection, setIsSlowConnection] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [nextRetryIn, setNextRetryIn] = useState(0);

    const retryTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const isHandlingOfflineRef = useRef(false);
    const intervalRef = useRef(null);
    const isPollingRef = useRef(isPolling);
    const abortControllerRef = useRef(null);
    const consecutiveErrorsRef = useRef(consecutiveErrors);
    const hasConnectedOnceRef = useRef(hasConnectedOnce);

    useEffect(() => { isPollingRef.current = isPolling; }, [isPolling]);
    useEffect(() => { consecutiveErrorsRef.current = consecutiveErrors; }, [consecutiveErrors]);
    useEffect(() => { hasConnectedOnceRef.current = hasConnectedOnce; }, [hasConnectedOnce]);

    useEffect(() => {
        loadInitialData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadInitialData = async () => {
        try {
            const data = await getStrategies();
            if (data) {
                if (!lastDay) setLastDay(data['date']);
                setIsOffline(false);
            } else {
                handleOffline();
            }
        } catch (error) {
            console.error('Errore caricamento dati iniziali:', error);
            handleOffline();
        }
    };

    const handleOffline = async () => {
        if (isHandlingOfflineRef.current) return;
        isHandlingOfflineRef.current = true;
        try {
            const alive = await checkHealth();
            if (alive) {
                setIsSlowConnection(true);
            } else {
                setIsOffline(true);
                scheduleRetry();
            }
        } finally {
            isHandlingOfflineRef.current = false;
        }
    };

    const handleOnline = () => {
        consecutiveErrorsRef.current = 0;
        setConsecutiveErrors(0);
        hasConnectedOnceRef.current = true;
        setHasConnectedOnce(true);
        setIsOffline(false);
        setIsSlowConnection(false);
        clearRetryTimeout();
    };

    const scheduleRetry = () => {
        clearRetryTimeout();
        setNextRetryIn(30);
        countdownIntervalRef.current = setInterval(() => {
            setNextRetryIn(prev => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        retryTimeoutRef.current = setTimeout(() => {
            loadInitialData();
        }, 30000);
    };

    const clearRetryTimeout = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setNextRetryIn(0);
    };

    const handleManualRetry = () => {
        clearRetryTimeout();
        loadInitialData();
    };

    useEffect(() => {
        let cancelled = false;

        const clearCurrentTimeout = () => {
            if (intervalRef.current) {
                clearTimeout(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const abortCurrentRequests = () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };

        clearCurrentTimeout();
        abortCurrentRequests();

        if (!lastDay) return;

        setHasFetched(false);

        const pollingFn = async () => {
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            try {
                const data = await getRecordsByDate(lastDay, signal);
                if (data) {
                    const accountRecords = data.filter(r =>
                        r.strategy === "AccountMonitor" && r.type === "PORTFOLIO_SNAPSHOT"
                    );
                    if (accountRecords.length > 0) {
                        const sorted = [...accountRecords].sort((a, b) =>
                            new Date(b.ts_event_iso) - new Date(a.ts_event_iso)
                        );
                        setSnapshot(sorted[0]);
                    } else {
                        setSnapshot(null);
                    }
                    setHasFetched(true);
                    setIsRefreshing(false);
                    handleOnline();
                }
            } catch (error) {
                if (cancelled) return;
                if (error.name === 'CanceledError' || error.name === 'AbortError') {
                    console.log('Richiesta annullata');
                } else {
                    if (!hasConnectedOnceRef.current) {
                        handleOffline();
                        return;
                    }
                    consecutiveErrorsRef.current += 1;
                    setConsecutiveErrors(consecutiveErrorsRef.current);
                    console.error(`Errore polling (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}):`, error);
                    if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
                        handleOffline();
                    }
                }
            }
        };

        const scheduleNextPoll = () => {
            if (cancelled || !isPollingRef.current || location.pathname !== '/account-balances') return;
            intervalRef.current = setTimeout(async () => {
                if (cancelled) return;
                intervalRef.current = null;
                await pollingFn();
                scheduleNextPoll();
            }, 3000);
        };

        pollingFn().then(() => {
            scheduleNextPoll();
        }).catch((error) => {
            if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
                console.error('Errore polling iniziale:', error);
            }
        });

        return () => {
            cancelled = true;
            clearCurrentTimeout();
            abortCurrentRequests();
        };
    }, [lastDay, isPolling, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => { clearRetryTimeout(); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (isOffline) {
        return <OfflineScreen onRetry={handleManualRetry} nextRetryIn={nextRetryIn}/>;
    }

    const isLoadingData = lastDay && !hasFetched && !isOffline;

    return (
        <Flex direction="column" h="100vh" overflow="hidden">
            {isSlowConnection && (
                <Alert status="warning" py={2}>
                    <AlertIcon/>
                    <Text fontSize="sm">Backend lento — ricaricamento in corso...</Text>
                </Alert>
            )}
            {isRefreshing && !isSlowConnection && (
                <Alert status="info" py={2}>
                    <AlertIcon/>
                    <Text fontSize="sm">Aggiornamento dati in corso...</Text>
                </Alert>
            )}
            <Box>
                <Toolbar last_day={lastDay} setDate={setLastDay}/>
            </Box>
            <Box flex="1" overflow="auto" p={{base: 2, md: 4}}>
                {isLoadingData ? (
                    <Center h="400px">
                        <Flex direction="column" align="center" gap={4}>
                            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#38A169" size="xl"/>
                            <Text color="gray.600" fontSize="md">Caricamento dati in corso...</Text>
                        </Flex>
                    </Center>
                ) : !snapshot ? (
                    <Center h="400px">
                        <Text color="gray.500" fontSize="md">Nessun dato disponibile per questa data</Text>
                    </Center>
                ) : (
                    <Box maxW="1200px">
                        {/* Header snapshot */}
                        <Flex align="center" mb={5} gap={3} flexWrap="wrap">
                            <Heading size="md" color="gray.700">Saldi Account</Heading>
                            <Badge colorScheme="green" fontSize="sm" px={2} py={1}>{snapshot.severity}</Badge>
                            <Text fontSize="sm" color="gray.500">
                                Ultimo snapshot: {new Date(snapshot.ts_event_iso).toLocaleString('it-IT')}
                            </Text>
                        </Flex>

                        {/* Bilanci */}
                        {snapshot.balances && snapshot.balances.length > 0 && (
                            <Box mb={6}>
                                <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Bilancio
                                </Heading>
                                <SimpleGrid columns={{base: 1, md: 2, lg: 3}} gap={4}>
                                    {snapshot.balances.map((balance, i) => {
                                        const freeAmt = parseAmount(balance.free);
                                        const lockedAmt = parseAmount(balance.locked);
                                        const totalAmt = freeAmt + lockedAmt;
                                        return (
                                            <Box
                                                key={i}
                                                p={5}
                                                borderRadius="lg"
                                                border="1px"
                                                borderColor="gray.200"
                                                bg="white"
                                                boxShadow="sm"
                                            >
                                                <Text fontSize="xl" fontWeight="bold" color="gray.800" mb={1}>
                                                    {balance.currency}
                                                </Text>
                                                <Text fontSize="2xl" fontWeight="extrabold" color="green.600" mb={3}>
                                                    {formatNumber(totalAmt)}
                                                </Text>
                                                <Divider mb={3}/>
                                                <Flex justify="space-between" mb={2}>
                                                    <Text fontSize="sm" color="gray.500">Disponibile</Text>
                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                                        {formatNumber(freeAmt)}
                                                    </Text>
                                                </Flex>
                                                <Flex justify="space-between">
                                                    <Text fontSize="sm" color="gray.500">Bloccato</Text>
                                                    <Text fontSize="sm" fontWeight="semibold" color="orange.500">
                                                        {formatNumber(lockedAmt)}
                                                    </Text>
                                                </Flex>
                                            </Box>
                                        );
                                    })}
                                </SimpleGrid>
                            </Box>
                        )}

                        {/* Posizioni Aperte */}
                        {snapshot.open_positions && snapshot.open_positions.length > 0 && (
                            <Box mb={6}>
                                <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Posizioni Aperte ({snapshot.open_positions.length})
                                </Heading>
                                <GenericTable items={snapshot.open_positions}/>
                            </Box>
                        )}

                        {/* Ordini Aperti */}
                        {snapshot.open_orders && snapshot.open_orders.length > 0 && (
                            <Box mb={6}>
                                <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Ordini Aperti ({snapshot.open_orders.length})
                                </Heading>
                                <GenericTable items={snapshot.open_orders}/>
                            </Box>
                        )}

                        {/* Trade Eseguiti */}
                        {snapshot.executed_trades && snapshot.executed_trades.length > 0 && (
                            <Box mb={6}>
                                <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Trade Eseguiti ({snapshot.executed_trades.length})
                                </Heading>
                                <GenericTable items={snapshot.executed_trades}/>
                            </Box>
                        )}

                        {/* Ordini Inviati */}
                        {snapshot.submitted_orders && snapshot.submitted_orders.length > 0 && (
                            <Box mb={6}>
                                <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Ordini Inviati ({snapshot.submitted_orders.length})
                                </Heading>
                                <GenericTable items={snapshot.submitted_orders}/>
                            </Box>
                        )}

                        {/* PnL */}
                        {(Object.keys(snapshot.realized_pnl || {}).length > 0 || Object.keys(snapshot.unrealized_pnl || {}).length > 0) && (
                            <Box mb={6}>
                                <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Profitti e Perdite
                                </Heading>
                                <SimpleGrid columns={{base: 1, md: 2}} gap={4}>
                                    {Object.keys(snapshot.realized_pnl || {}).length > 0 && (
                                        <Box p={4} borderRadius="lg" border="1px" borderColor="gray.200" bg="white" boxShadow="sm">
                                            <Text fontWeight="bold" mb={3} color="gray.700">PnL Realizzato</Text>
                                            <Divider mb={3}/>
                                            {Object.entries(snapshot.realized_pnl).map(([key, value]) => (
                                                <Flex key={key} justify="space-between" mb={2}>
                                                    <Text fontSize="sm" color="gray.500">{key}</Text>
                                                    <Text fontSize="sm" fontWeight="semibold">
                                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                    </Text>
                                                </Flex>
                                            ))}
                                        </Box>
                                    )}
                                    {Object.keys(snapshot.unrealized_pnl || {}).length > 0 && (
                                        <Box p={4} borderRadius="lg" border="1px" borderColor="gray.200" bg="white" boxShadow="sm">
                                            <Text fontWeight="bold" mb={3} color="gray.700">PnL Non Realizzato</Text>
                                            <Divider mb={3}/>
                                            {Object.entries(snapshot.unrealized_pnl).map(([key, value]) => (
                                                <Flex key={key} justify="space-between" mb={2}>
                                                    <Text fontSize="sm" color="gray.500">{key}</Text>
                                                    <Text fontSize="sm" fontWeight="semibold">
                                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                    </Text>
                                                </Flex>
                                            ))}
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </Box>
                        )}

                        {/* Nessuna attività aperta */}
                        {snapshot.open_positions?.length === 0 &&
                         snapshot.open_orders?.length === 0 &&
                         snapshot.executed_trades?.length === 0 &&
                         snapshot.submitted_orders?.length === 0 &&
                         Object.keys(snapshot.realized_pnl || {}).length === 0 &&
                         Object.keys(snapshot.unrealized_pnl || {}).length === 0 && (
                            <Box p={4} borderRadius="lg" border="1px" borderColor="gray.200" bg="gray.50">
                                <Text color="gray.500" fontSize="sm" textAlign="center">
                                    Nessuna posizione, ordine o trade in questo snapshot
                                </Text>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Flex>
    );
};

export default AccountBalances;
